import { documentToHtmlString } from '@contentful/rich-text-html-renderer'
import { FlexContainer } from '@news12/storybook-news'

import React, { useState, useEffect } from 'react'

import internalApiQueryWrapper from '../../core/internalApiQueryWrapper'
import useMediaQuery from '../../hooks/useMediaQuery'
import useRegion from '../../hooks/useRegion'
import collectionTitle from '../../styles/components/homePage/collection_title'
import homePageStyle from '../../styles/components/homePage/home_page'
import horizontalCard from '../../styles/components/homePage/horizontal_card'
import dynamicCard from '../../styles/components/homePage/topStories/dynamic_card'
import rightAlignedCard from '../../styles/components/homePage/topStories/right_aligned_card'
import BaseCSS from '../../styles/global/BaseCSS'
import AdUnit from '../Ads/AdUnit'
import SideNavFooter from '../Header/SideNavFooter'
import Title from '../HomePage/Title'
import Loading from '../Loading'
import Metadata from '../Metadata'
import UnresetCSS from '../UnresetCSS'
import Card from './Card'
import LoadMoreButton from './LoadMoreButton'
import TopStories from './TopStories'

import Head from 'next/head'
import { useRouter } from 'next/router'

const configureProps = (index, isMobileScreen) => {
  if (isMobileScreen) {
    return { style: index === 0 ? dynamicCard : horizontalCard }
  }

  return index % 4 === 0 ? { style: rightAlignedCard, isHeroCard: true } : { style: horizontalCard }
}

export default ({ data: { stories = [], total }, params, body, metadata, hero }) => {
  const [collection, setCollection] = useState(stories)
  const [topStories, setTopStories] = useState([])
  const [variant, setVariant] = useState(0)
  const [loading, setLoading] = useState(false)
  const isMobileScreen = useMediaQuery(768)
  const router = useRouter()
  const region = useRegion()

  useEffect(() => {
    const fetchData = async () => {
      const data = await internalApiQueryWrapper('/api/contentful/topStories', {
        region: region.name
      })
      setTopStories(data?.stories)
    }

    fetchData().catch(err => console.log(err))
  }, [])

  const loadMore = async () => {
    setLoading(true)

    const data = await internalApiQueryWrapper('/api/contentful/collection', {
      skip: collection.length,
      ...(variant == 1 ? { limit: 12 } : {}),
      ...(params.categories ? { categories: params.categories } : {}),
      ...(params.regions ? { regions: params.regions } : {})
    })
    setLoading(false)
    setCollection([...collection, ...data.stories])
  }

  if (!collection.length) {
    return <Loading />
  }

  const prefix = 'News12 | '
  const hasPrefix = /^news12\s*\|\s*/i.test(metadata.title ?? '')

  return (
    <>
      <Head>
        <title>{hasPrefix ? metadata.title : `${prefix}${metadata.title}`}</title>
        <link rel='canonical' href={`https://${region.name}.news12.com${router.asPath.split('?')[0]}`} />
      </Head>
      <Metadata metadata={metadata} />
      <div className='main-layout-container'>
        <div className='main-layout'>
          <div className='ad-banner-top-container'>
            <AdUnit adUnitTemplate='leaderboard' id={'ad-banner-top'} type='leaderboardRedesign' />
          </div>
          {hero ? <img src={hero} alt={metadata.title} className='hero' /> : null}
          <div className='category'>
            <Title title={metadata.title} />
            <div className='category-body' dangerouslySetInnerHTML={{ __html: documentToHtmlString(body) || null }} />
            {collection.map((story, index) => (
              <React.Fragment key={`story-card-container-${index}`}>
                {index % 4 === 0 && index !== 0 && (
                  <div className='category-middleboard'>
                    <AdUnit adUnitTemplate='leaderboard' id={'ad-banner-middle-1'} type='leaderboardNew' />
                  </div>
                )}
                <Card {...configureProps(index, isMobileScreen)} story={story} key={story.slug} />
              </React.Fragment>
            ))}
          </div>
          {total > collection.length && <LoadMoreButton loading={loading} loadMore={loadMore} />}
          <FlexContainer className={'banner-middle'} justifyContent={'center'} margin={'16px 0'}>
            <AdUnit
              adUnitTemplate='leaderboard'
              id='ad-banner-middle-1'
              type='leaderboardDynamic'
              uniqueIdentifier='more-stories'
            />
          </FlexContainer>
          {topStories?.length ? <TopStories collection={topStories} /> : null}
        </div>
        <div className='main-layout-sidebar'>
          <AdUnit adUnitTemplate='rectangleVertical' id={'ad-banner-sidebar-top'} type='homePageSidebar' />
        </div>
        <UnresetCSS />
        <BaseCSS />
        <style jsx>{homePageStyle}</style>
        <style jsx>{rightAlignedCard}</style>
        <style jsx>{horizontalCard}</style>
        <style jsx>{collectionTitle}</style>
        <style jsx>{`
          .ad-banner-top-container {
            width: 100%;
          }
          @media (max-width: 768px) {
            .ad-banner-top-container {
              display: flex;
              justify-content: center;
              text-align: center;
            }
          }
        `}</style>
      </div>
      {isMobileScreen && <SideNavFooter />}
    </>
  )
}
